import { Controller } from '@hotwired/stimulus';

// Controller for dynamically updating the order sidebar when recommendations are selected
// Also handles time-based filtering of recommendations to prevent collisions with next appointment
export default class extends Controller {
  static targets = [
    'checkbox',
    'sidebarItems',
    'subtotal',
    'vat',
    'total',
    'netto',
    'timeWarning'
  ];
  static values = {
    basePrice: Number,
    imagePackagePriceCents: { type: Number, default: 0 },
    discountCents: { type: Number, default: 0 },
    vatRate: { type: Number, default: 0.19 },
    availableMinutes: { type: Number, default: -1 }, // -1 means unlimited
    baseDuration: { type: Number, default: 0 }
  };

  connect() {
    this.updateSidebar();
    this.updateTimeConstraints();
  }

  toggle(event) {
    this.updateSidebar();
    this.updateTimeConstraints();
  }

  // Calculate total duration of selected recommendations and disable those that would exceed available time
  updateTimeConstraints() {
    // -1 means unlimited time available
    if (this.availableMinutesValue < 0) return;

    // Calculate total duration of currently selected recommendations
    let selectedDuration = 0;
    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.checked && !checkbox.disabled) {
        const duration = parseInt(checkbox.dataset.recommendationDuration, 10) || 0;
        selectedDuration += duration;
      }
    });

    const remainingMinutes = this.availableMinutesValue - selectedDuration;

    // Update each unchecked checkbox - disable if its duration exceeds remaining time
    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.checked) return; // Skip already selected
      if (checkbox.dataset.originallyDisabled === 'true') return; // Skip originally disabled

      const duration = parseInt(checkbox.dataset.recommendationDuration, 10) || 0;
      const label = checkbox.closest('label');

      if (duration > remainingMinutes) {
        checkbox.disabled = true;
        if (label) {
          label.classList.add('booking-form__checkbox--disabled');
          // Add or update hint
          let hint = label.querySelector('.booking-form__checkbox-hint--dynamic');
          if (!hint) {
            hint = document.createElement('span');
            hint.className =
              'booking-form__checkbox-hint booking-form__checkbox-hint--dynamic';
            label.appendChild(hint);
          }
          hint.textContent = 'Nicht verfügbar - kollidiert mit nächstem Termin';
        }
      } else {
        checkbox.disabled = false;
        if (label) {
          label.classList.remove('booking-form__checkbox--disabled');
          const hint = label.querySelector('.booking-form__checkbox-hint--dynamic');
          if (hint) hint.remove();
        }
      }
    });
  }

  updateSidebar() {
    let subtotalCents =
      (this.basePriceValue || 0) +
      (this.imagePackagePriceCentsValue || 0) -
      (this.discountCentsValue || 0);
    const selectedItems = [];

    // Find all checked checkboxes
    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        const name = checkbox.dataset.recommendationName;
        const priceCents = parseInt(checkbox.dataset.recommendationPrice, 10) || 0;

        subtotalCents += priceCents;
        selectedItems.push({ name, priceCents });
      }
    });

    // Update sidebar items display
    if (this.hasSidebarItemsTarget) {
      if (selectedItems.length > 0) {
        this.sidebarItemsTarget.innerHTML = selectedItems
          .map(
            (item) => `
          <div class="order-item flex justify-between py-2 border-b">
            <div class="order-item-name">
              <span>${item.name}</span>
            </div>
            <div class="order-item-price text-right">
              ${this.formatCurrency(item.priceCents)}
            </div>
          </div>
        `
          )
          .join('');
        this.sidebarItemsTarget.classList.remove('hidden');
      } else {
        this.sidebarItemsTarget.innerHTML = '';
        this.sidebarItemsTarget.classList.add('hidden');
      }
    }

    // Calculate VAT and net
    const vatCents = Math.round(
      (subtotalCents * this.vatRateValue) / (1 + this.vatRateValue)
    );
    const netCents = subtotalCents - vatCents;

    // Update totals display
    if (this.hasNettoTarget) {
      this.nettoTarget.textContent = this.formatCurrency(netCents);
    }
    if (this.hasVatTarget) {
      this.vatTarget.textContent = this.formatCurrency(vatCents);
    }
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = this.formatCurrency(subtotalCents);
    }
    if (this.hasSubtotalTarget) {
      this.subtotalTarget.textContent = this.formatCurrency(subtotalCents);
    }
  }

  formatCurrency(cents) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }
}
